import { useRef } from 'react';

import CloseIcon from '@amazd/common/icons/CloseIcon';
import { useTranslation, Trans } from '@amazd/common/utils/i18n';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Typography } from '@mui/material';
import useCompanyImageUpload, { CompanyImageUploadPurposeEnum } from 'hooks/useCompanyImageUpload';

import useStyles from './styles';

interface Props {
  companyId: string;
  purpose?: CompanyImageUploadPurposeEnum;
  open: boolean;
  isRemovable: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
  onRemove: () => void;
}

const UploadModal = ({ open, onClose, isRemovable, purpose, companyId, onChange, onRemove }: Props) => {
  const classes = useStyles();
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const { uploadImage, uploading, error } = useCompanyImageUpload();
  const { t } = useTranslation();

  if (!purpose) {
    console.error('You must provide `purpose` prop when using with api v1');
    return null;
  }

  const handleOnFilesSelected = async (e: any) => {
    const [file]: [File] = e.target.files;
    if (!file) {
      return;
    }

    const url = (
      await uploadImage({
        variables: {
          purpose,
          companyId,
          upload: file,
        },
      })
    ).data?.companyImageUpload;

    if (!url) {
      return;
    }
    onChange?.(url);
  };

  return (
    <Dialog open={open} className={classes.modal}>
      <Typography variant="h6" className={classes.modalTitle}>
        {t('widget-configurator-upload-image-modal-title')}
      </Typography>
      <IconButton color="primary" onClick={onClose} className={classes.modalCloseButton}>
        <CloseIcon style={{ fontSize: 21 }} />
      </IconButton>
      <DialogContent className={classes.modalContent}>
        <Typography color="textSecondary">
          <Trans i18nKey="widget-configurator-upload-image-modal-description">
            Please upload a PNG, JPEG or other image file.
            <br />
            Max size allowed: 10MB
          </Trans>
        </Typography>
        {error && (
          <Typography className={classes.modalError} color="error">
            {t('widget-configurator-upload-image-error')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        {isRemovable && (
          <Button color="primary" variant="outlined" onClick={onRemove}>
            {t('widget-configurator-upload-image-remove-button')}
          </Button>
        )}
        <Button
          component="label"
          htmlFor="upload-modal-input-file"
          disabled={uploading}
          className={classes.modalSubmit}
          color="primary"
          variant="contained"
        >
          {t('widget-configurator-upload-image-upload-button')}
          {uploading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Button>
        <input
          id="upload-modal-input-file"
          type="file"
          hidden
          accept="image/*"
          ref={hiddenFileInput}
          onChange={handleOnFilesSelected}
        />
      </DialogActions>
    </Dialog>
  );
};

export default UploadModal;
